import { supabase } from '../client';
import type { Payment, Subscription, SubscriptionPlan, SubscriptionUsageStat } from '@/lib/payment/types';
import { logError } from '@/lib/sentry';

export class PaymentRepository {
  // Payment 관련 메서드
  async createPayment(payment: Omit<Payment, 'id' | 'created_at' | 'modified_at'>): Promise<Payment> {
    const { data, error } = await supabase
      .from('payment')
      .insert({
        ...payment,
        created_at: new Date(),
        modified_at: new Date()
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async updatePaymentStatus(
    paymentId: string, 
    status: Payment['status'], 
    paidAt?: Date, 
    pgTransactionId?: string
  ): Promise<Payment> {
    const { data, error } = await supabase
      .from('payment')
      .update({
        status,
        paid_at: paidAt,
        pg_transaction_id: pgTransactionId,
        modified_at: new Date()
      })
      .eq('payment_id', paymentId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async getPaymentById(paymentId: string): Promise<Payment | null> {
    const { data, error } = await supabase
      .from('payment')
      .select()
      .eq('payment_id', paymentId)
      .single();

    if (error) return null;
    return data;
  }

  // Subscription 관련 메서드
  async createSubscription(subscription: Omit<Subscription, 'id' | 'created_at' | 'modified_at'>): Promise<Subscription> {
    const { data, error } = await supabase
      .from('subscription')
      .insert({
        ...subscription,
        downgrade_to: subscription.downgrade_to || null,
        created_at: new Date(),
        modified_at: new Date()
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async getCurrentSubscription(userId: string): Promise<Subscription | null> {
    try {
      const { data, error } = await supabase
        .from('subscription')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // 데이터가 없는 경우 (not found)
          return null;
        }
        throw error;
      }

      return data as Subscription;
    } catch (error) {
      // 치명적인 에러일 때만 로그 남김
      if (error instanceof Error && error.message !== 'No rows returned') {
        logError('구독 정보 조회 중 오류 발생:', error);
      }
      return null;
    }
  }

  /**
   * 현재 past_due 상태의 구독 조회
   * 결제 실패 상태인 구독을 우선적으로 표시하기 위함
   */
  async getPastDueSubscription(userId: string): Promise<Subscription | null> {
    try {
      const { data, error } = await supabase
        .from('subscription')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'past_due')
        // user-billing-retry의 canonical 행 선택과 동일 정렬 —
        // 화면에 표시하는 행과 서버가 청구하는 행이 항상 일치해야 한다
        .order('period_end', { ascending: false })
        .order('id', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // 데이터가 없는 경우 (not found)
          return null;
        }
        throw error;
      }

      return data as Subscription;
    } catch (error) {
      // 치명적인 에러일 때만 로그 남김
      if (error instanceof Error && error.message !== 'No rows returned') {
        logError('past_due 구독 정보 조회 중 오류 발생:', error);
      }
      return null;
    }
  }

  async updateSubscriptionStatus(
    subscriptionId: number, 
    status: Subscription['status'],
    cancelAtPeriodEnd?: boolean,
    downgradeTo?: string | null
  ): Promise<Subscription> {
    const { data, error } = await supabase
      .from('subscription')
      .update({
        status,
        cancel_at_period_end: cancelAtPeriodEnd,
        downgrade_to: downgradeTo,
        modified_at: new Date().toISOString()
      })
      .eq('id', subscriptionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * 결제 실패 시 구독 상태를 past_due로 변경
   */
  async markSubscriptionAsPastDue(subscriptionId: number): Promise<Subscription> {
    return this.updateSubscriptionStatus(subscriptionId, 'past_due');
  }

  /**
   * past_due 상태의 구독을 다시 active로 변경 (결제 재시도 후 성공 시)
   */
  async reactivateSubscription(subscriptionId: number): Promise<Subscription> {
    return this.updateSubscriptionStatus(subscriptionId, 'active');
  }

  async updateSubscriptionPeriod(
    subscriptionId: number,
    periodStart: Date,
    periodEnd: Date
  ): Promise<Subscription> {
    const { data, error } = await supabase
      .from('subscription')
      .update({
        period_start: periodStart.toISOString(),
        period_end: periodEnd.toISOString(),
        modified_at: new Date().toISOString()
      })
      .eq('id', subscriptionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // SubscriptionPlan 관련 메서드
  async getSubscriptionPlan(planId: string): Promise<SubscriptionPlan | null> {
    const { data, error } = await supabase
      .from('subscription_plan')
      .select()
      .eq('id', planId)
      .single();

    if (error) return null;
    return data;
  }

  async getAllSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    const { data, error } = await supabase
      .from('subscription_plan')
      .select()
      .order('price');

    if (error) throw error;
    return data;
  }

  // SubscriptionUsageStat 관련 메서드
  async createOrUpdateUsageStat(usageStat: Omit<SubscriptionUsageStat, 'id' | 'created_at' | 'modified_at'>): Promise<SubscriptionUsageStat> {
    const { data: existing } = await supabase
      .from('subscription_usage_stat')
      .select()
      .eq('user_id', usageStat.user_id)
      .eq('subscription_id', usageStat.subscription_id)
      .single();

    if (existing) {
      const { data, error } = await supabase
        .from('subscription_usage_stat')
        .update({
          ...usageStat,
          copy_used_count: usageStat.copy_used_count || 0, // Ensure default
          modified_at: new Date()
        })
        .eq('id', existing.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } else {
      const { data, error } = await supabase
        .from('subscription_usage_stat')
        .insert({
          ...usageStat,
          copy_used_count: usageStat.copy_used_count || 0, // Ensure default
          created_at: new Date(),
          modified_at: new Date()
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    }
  }

  async getCurrentUsageStat(userId: string, subscriptionId: number): Promise<SubscriptionUsageStat | null> {
    const { data, error } = await supabase
      .from('subscription_usage_stat')
      .select()
      .eq('user_id', userId)
      .eq('subscription_id', subscriptionId)
      .single();

    if (error) return null;
    return data;
  }

  async incrementUsageCount(usageStatId: number): Promise<SubscriptionUsageStat> {
    const { data, error } = await supabase.rpc('increment_usage_count', {
      row_id: usageStatId
    });

    if (error) throw error;
    return data;
  }

  async incrementCopyUsageCount(usageStatId: number): Promise<SubscriptionUsageStat> {
    const { data, error } = await supabase.rpc('increment_copy_usage_count', {
      row_id: usageStatId
    });

    if (error) throw error;
    // Assuming the RPC function returns the updated row, adjust if not
    // If it doesn't return the row, we might need to fetch it again or adjust the return type
    // For now, let's assume it returns the necessary fields for SubscriptionUsageStat
    // This might need adjustment based on the actual RPC function's return value.
    // A simple approach if it returns a boolean or count:
    // return this.getUsageStatById(usageStatId); // You'd need a getUsageStatById method
    // For now, casting, but this is a placeholder for actual return handling.
    return data as SubscriptionUsageStat;
  }

  // billing_key 테이블에 빌링키 정보 저장하는 메서드 추가
  async saveBillingKey(userId: string, billingKey: string): Promise<void> {
    // 기존 빌링키가 있다면 삭제
    await supabase
      .from('billing_key')
      .delete()
      .eq('user_id', userId);

    // 새 빌링키 저장
    const { error } = await supabase
      .from('billing_key')
      .insert([
        { 
          user_id: userId, 
          key: billingKey,
          created_at: new Date()
        }
      ]);

    if (error) {
      logError('빌링키 저장 오류:', error);
      throw new Error('빌링키를 저장하는 중 오류가 발생했습니다');
    }
  }

  // billing_key 테이블에서 빌링키 조회하는 메서드 추가
  async getBillingKey(userId: string) {
    return await supabase
      .from('billing_key')
      .select('*')
      .eq('user_id', userId)
      .single();
  }
} 